<template>
  <!-- 관리자 예약 상세 '현장 추가'(결정장부 D37): 매장에서 정한 사람 추가·드레스만 빌려 입기 수를 고르면 받을 금액을 보이고 손님 결제 QR을 띄운다. -->
  <v-dialog :value="true" width="460" max-width="calc(100vw - 32px)" content-class="rounded-xl" persistent @keydown.esc="close">
    <v-card>
      <v-card-title>현장 추가</v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle class="black--text text-body-1 font-weight-medium mt-2" style="word-break: keep-all">
        {{ $getUserDisplayName(reservation.name) }} 예약 · 지금 {{ cloth }}명
      </v-card-subtitle>

      <v-card-text v-if="phase === 'pick'" class="px-6">
        <div class="pick-row">
          <div class="pick-label">
            <span class="pick-name" id="onsite-persons-label">사람 추가</span>
            <span class="pick-hint">{{ isPremium ? "같이 찍을 사람" : "프리미엄샷 예약만 더할 수 있습니다" }}</span>
          </div>
          <div class="pick-stepper" role="group" aria-labelledby="onsite-persons-label">
            <v-btn icon outlined class="pick-btn" aria-label="사람 추가 줄이기" :disabled="!isPremium || persons <= 0" @click="step('persons', -1)"><v-icon>mdi-minus</v-icon></v-btn>
            <output class="pick-count" aria-live="polite">{{ persons }}명</output>
            <v-btn icon outlined class="pick-btn" aria-label="사람 추가 늘리기" :disabled="!isPremium || persons >= personLimit" @click="step('persons', 1)"><v-icon>mdi-plus</v-icon></v-btn>
          </div>
        </div>
        <div class="pick-row">
          <div class="pick-label">
            <span class="pick-name" id="onsite-dresses-label">드레스만 빌려 입기</span>
            <span class="pick-hint">사진 없이 드레스만 입는 사람</span>
          </div>
          <div class="pick-stepper" role="group" aria-labelledby="onsite-dresses-label">
            <v-btn icon outlined class="pick-btn" aria-label="드레스만 빌려 입기 줄이기" :disabled="dresses <= 0" @click="step('dresses', -1)"><v-icon>mdi-minus</v-icon></v-btn>
            <output class="pick-count" aria-live="polite">{{ dresses }}명</output>
            <v-btn icon outlined class="pick-btn" aria-label="드레스만 빌려 입기 늘리기" :disabled="dresses >= MAX" @click="step('dresses', 1)"><v-icon>mdi-plus</v-icon></v-btn>
          </div>
        </div>

        <div class="quote-box" :class="{ 'is-waiting': quoting && !quote }" :aria-busy="quoting ? 'true' : 'false'">
          <v-progress-linear v-if="quoting" indeterminate height="2" color="#7d1832" class="quote-wait" />
          <p v-if="!persons && !dresses" class="text-body-2 grey--text text--darken-1 mb-0">더할 사람이나 드레스 수를 골라 주세요.</p>
          <p v-else-if="quoting && !quote" class="text-body-2 grey--text text--darken-1 mb-0">금액을 확인하고 있습니다.</p>
          <dl v-else-if="quote" class="diff-table" :class="{ 'is-stale': quoting }">
            <div v-if="quote.persons">
              <dt>
                사람 {{ quote.persons }}명 더
                <span class="diff-sub">정가 {{ quote.fromCloth + quote.persons }}명 {{ won(quote.personTo) }} − {{ quote.fromCloth }}명 {{ won(quote.personFrom) }}</span>
              </dt>
              <dd>{{ won(quote.personAmount) }}</dd>
            </div>
            <div v-if="quote.dresses">
              <dt>
                드레스만 빌려 입기 {{ quote.dresses }}명
                <span class="diff-sub">{{ quote.dressTitle && quote.dressTitle.kr }} 1인 {{ won(quote.dressUnit) }}</span>
              </dt>
              <dd>{{ won(quote.dressAmount) }}</dd>
            </div>
            <div class="diff-total">
              <dt>받을 금액</dt>
              <dd>{{ won(quote.amount) }}</dd>
            </div>
          </dl>
        </div>
        <p v-if="freeAdd" class="text-body-2 grey--text text--darken-1 mb-0 mt-4" style="word-break: keep-all">
          정가표에서 이 인원은 총액이 같아 받을 금액이 없습니다. 결제 없이 예약 인원만 늘립니다.
        </p>
        <p v-else-if="quote && quote.amount > 0" class="text-body-2 grey--text text--darken-1 mb-0 mt-4" style="word-break: keep-all">
          QR을 띄워 손님 휴대폰으로 결제받습니다.<template v-if="quote.persons"> 결제가 끝나면 예약 인원이 바로 늘어납니다.</template>
        </p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'qr'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-center">
        <AddonPayQr :url="payUrl" :amount-text="won(order.amount)" :waiting="QR_TEXT.waiting">
          <span>손님 휴대폰 카메라로 찍게 해 주세요. 결제가 끝나면 이 창이 저절로 바뀝니다.</span>
          <span v-if="ordered.persons">창을 닫아도 손님이 결제를 마치면 예약 인원은 늘어납니다.</span>
        </AddonPayQr>
      </v-card-text>

      <v-card-text v-else-if="phase === 'paid'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-body-1 font-weight-bold black--text" style="word-break: keep-all">{{ paidText }}</v-card-text>

      <v-card-text v-else-if="phase === 'paidNotApplied'" class="px-6 text-body-1 font-weight-bold black--text" style="word-break: keep-all">{{ paidOnlyText }}</v-card-text>

      <!-- 화면 읽기 프로그램이 단계가 바뀐 것을 읽도록, 알림 자리는 늘 두고 글만 바꾼다 -->
      <p class="d-sr-only" role="status">{{ statusText }}</p>
      <div class="px-6" :class="{ 'text-center': phase === 'qr' }" role="alert">
        <p v-if="alertText" class="pt-3 mb-0 text-body-2 font-weight-bold" style="color: #a53a66; word-break: keep-all">{{ alertText }}</p>
      </div>

      <v-card-actions class="pb-4">
        <v-spacer />
        <v-btn ref="closeBtn" class="px-4" depressed outlined :disabled="busy" :loading="busy && phase === 'qr'" @click="close">닫기</v-btn>
        <v-btn v-if="phase === 'pick' && freeAdd" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" :disabled="quoting || quote.persons !== persons" @click="addFree">인원만 늘리기</v-btn>
        <v-btn v-else-if="phase === 'pick'" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" :disabled="!canOrder" @click="createOrder">QR 띄우기</v-btn>
        <v-btn v-if="phase === 'paidNotApplied'" ref="applyBtn" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" @click="applyOnsite">인원 반영</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AddonPayQr from "./AddonPayQr"
import addonQrPay, { REQUEST_TIMEOUT_MS } from "./addonQrPay"

// 서버가 한 번에 받는 수(사람·드레스 각각)
const MAX = 6
// 수를 바꾼 뒤 이만큼 멈추면 금액을 묻는다(여러 번 눌러도 한 번만 묻게)
const QUOTE_DELAY_MS = 250
// 이 화면에서 이미 띄운 현장 추가 주문(예약·수·금액이 같으면 같은 QR을 다시 띄운다. 컨셉 변경 차액 창과 같은 까닭)
const OPEN_ORDERS = new Map()
const TEXT = {
  notApplied: "결제는 받았지만 예약 인원이 늘지 않았습니다. '인원 반영'을 눌러 주세요.",
  orderFailed: "결제 QR을 만들지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
  quoteFailed: "금액을 확인하지 못했습니다. 수를 다시 골라 주세요.",
  noAmount: "받을 금액이 없습니다.",
  applyFailed: "인원을 늘리지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
}

export default {
  name: "OnsiteAddDialog",

  components: { AddonPayQr },

  mixins: [addonQrPay],

  props: {
    // 관리자 예약 목록의 한 줄(id가 예약 번호)
    reservation: { type: Object, required: true },
  },

  data: () => ({
    MAX,
    phase: "pick", // pick | qr | paid | paidNotApplied
    persons: 0,
    dresses: 0,
    quote: null,
    quoting: false,
    quoteError: "",
    quoteSeq: 0,
    quoteTimer: null,
    // QR을 띄운 그 주문의 수(창에서 수를 바꿔도 결제 받음 글은 이 수로)
    ordered: { persons: 0, dresses: 0, fromCloth: 0 },
    orderKeyUsed: "",
    // 서버가 알려 준 이 상품의 최대 인원(사람 추가 금액을 처음 물은 뒤부터 '+'를 그 앞에서 잠근다)
    maxPersons: 0,
    // 결제 없이 인원만 늘린 경우
    freeDone: false,
    // '인원 반영'이 막힌 까닭(안 늚 알림 뒤에 붙인다)
    applyError: "",
  }),

  computed: {
    cloth() {
      return Math.max(1, Number(this.reservation.cloth) || 1)
    },
    personLimit() {
      return this.maxPersons ? Math.max(0, Math.min(MAX, this.maxPersons - this.cloth)) : MAX
    },
    isPremium() {
      return String(this.reservation.productType || "").trim().toLowerCase() === "premium"
    },
    // 정가표에서 사람을 더해도 총액이 늘지 않는 경우(드레스 없음): 결제 없이 인원만 늘린다
    freeAdd() {
      // 다시 묻는 동안에도 단추 이름은 그대로 두고 잠그기만 한다(이름이 깜빡이지 않게)
      return Boolean(this.quote) && this.quote.amount === 0 && this.quote.persons > 0 && !this.quote.dresses && !this.dresses
    },
    canOrder() {
      return !this.quoting && !this.busy && Boolean(this.quote) && this.quote.amount > 0 && this.quote.persons === this.persons && this.quote.dresses === this.dresses
    },
    // 인원이 아직 안 늘었을 때 본문: 받은 것과 내어 줄 드레스만
    paidOnlyText() {
      const lines = [`${this.won(this.order && this.order.amount)} 결제를 받았습니다.`]
      if (this.ordered.dresses) lines.push(`드레스만 빌려 입기 ${this.ordered.dresses}명분을 내어 주세요.`)
      return lines.join(" ")
    },
    paidText() {
      if (this.freeDone) return `결제 없이 예약 인원을 ${this.ordered.fromCloth + this.ordered.persons}명으로 늘렸습니다.`
      const lines = [`${this.won(this.order && this.order.amount)} 결제를 받았습니다.`]
      if (this.ordered.persons) lines.push(`예약 인원을 ${this.ordered.fromCloth + this.ordered.persons}명으로 늘렸습니다.`)
      if (this.ordered.dresses) lines.push(`드레스만 빌려 입기 ${this.ordered.dresses}명분을 내어 주세요.`)
      return lines.join(" ")
    },
    alertText() {
      if (this.errorMessage) return this.errorMessage
      if (this.phase === "pick" && this.quoteError) return this.quoteError
      if (this.phase === "pick" && this.quote && !this.quoting && !(this.quote.amount > 0) && !this.freeAdd) return TEXT.noAmount
      if (this.phase === "paidNotApplied") return this.applyError ? `${TEXT.notApplied} ${this.applyError}` : TEXT.notApplied
      if (this.qrOffline) return this.QR_TEXT.offline
      return ""
    },
  },

  beforeDestroy() {
    clearTimeout(this.quoteTimer)
  },

  methods: {
    payParam() {
      return "onsite"
    },

    statusUrl() {
      return this.statusUrlOf(this.order)
    },

    statusUrlOf(order) {
      return `/api/addon-pay/onsite/${order.orderId}`
    },

    // 결제됐는데 인원이 안 늚 → '인원 반영', QR 기다림·결제 받음 → 그 단계 본문
    focusTarget() {
      if (this.phase === "paidNotApplied") return this.$refs.applyBtn
      return this.$refs.phaseBody
    },

    step(key, delta) {
      this[key] = Math.min(MAX, Math.max(0, this[key] + delta))
      this.errorMessage = ""
      this.quoteError = ""
      clearTimeout(this.quoteTimer)
      if (!this.persons && !this.dresses) {
        this.quote = null
        this.quoting = false
        this.quoteSeq += 1
        return
      }
      this.quoting = true
      this.quoteTimer = setTimeout(() => this.askQuote(), QUOTE_DELAY_MS)
    },

    // 고른 수로 받을 금액을 묻는다. 늦게 온 앞 답은 버린다.
    async askQuote() {
      const seq = ++this.quoteSeq
      const input = { reservationId: this.reservation.id, persons: this.persons, dresses: this.dresses }
      try {
        const { data } = await this.$http.post("/api/addon-pay/onsite/quote", input, { timeout: REQUEST_TIMEOUT_MS })
        if (seq !== this.quoteSeq) return
        this.quote = data
        if (data.maxPersons) this.maxPersons = data.maxPersons
      } catch (error) {
        if (seq !== this.quoteSeq) return
        const body = (error && error.response && error.response.data) || {}
        this.quote = null
        if (body.maxPersons) {
          this.maxPersons = body.maxPersons
          if (this.persons > this.personLimit) {
            this.persons = this.personLimit
            this.quoting = false
            if (this.persons || this.dresses) {
              this.quoting = true
              this.quoteTimer = setTimeout(() => this.askQuote(), QUOTE_DELAY_MS)
            }
            return
          }
        }
        this.quoteError = body.message || TEXT.quoteFailed
      } finally {
        if (seq === this.quoteSeq) this.quoting = false
      }
    },

    orderKey() {
      return `${this.reservation.id}:${this.quote.fromCloth}:${this.quote.persons}:${this.quote.dresses}:${this.quote.amount}`
    },

    async createOrder() {
      if (!this.canOrder) return
      this.busy = true
      this.errorMessage = ""
      const key = this.orderKey()
      try {
        let order = OPEN_ORDERS.get(key)
        if (order && !(await this.stillOpen(order))) {
          OPEN_ORDERS.delete(key)
          order = null
        }
        if (!order) {
          const { data } = await this.$http.post(
            "/api/addon-pay/onsite/order",
            { reservationId: this.reservation.id, persons: this.quote.persons, dresses: this.quote.dresses },
            { timeout: REQUEST_TIMEOUT_MS }
          )
          order = data
          OPEN_ORDERS.set(key, order)
        }
        this.order = order
        this.orderKeyUsed = key
        const quote = order.quote || this.quote
        this.ordered = { persons: quote.persons, dresses: quote.dresses, fromCloth: quote.fromCloth }
        this.phase = "qr"
        this.poll()
      } catch (error) {
        const body = (error && error.response && error.response.data) || {}
        if (body.code === "NO_AMOUNT" && body.quote) this.quote = body.quote
        this.errorMessage = body.code === "NO_AMOUNT" ? TEXT.noAmount : body.message || TEXT.orderFailed
      } finally {
        this.busy = false
      }
    },

    finish(data) {
      clearTimeout(this.timer)
      OPEN_ORDERS.delete(this.orderKeyUsed)
      if (!data.applied) {
        this.phase = "paidNotApplied"
        return
      }
      this.phase = "paid"
      this.$emit("changed", { cloth: this.ordered.fromCloth + this.ordered.persons, persons: this.ordered.persons })
    },

    async addFree() {
      if (this.busy || !this.freeAdd || this.quoting || this.quote.persons !== this.persons) return
      this.busy = true
      this.errorMessage = ""
      try {
        await this.$http.post("/api/addon-pay/onsite/free", { reservationId: this.reservation.id, persons: this.quote.persons, dresses: 0, fromCloth: this.quote.fromCloth }, { timeout: REQUEST_TIMEOUT_MS })
        this.ordered = { persons: this.quote.persons, dresses: 0, fromCloth: this.quote.fromCloth }
        this.freeDone = true
        this.phase = "paid"
        this.$emit("changed", { cloth: this.ordered.fromCloth + this.ordered.persons, persons: this.ordered.persons })
      } catch (error) {
        const body = (error && error.response && error.response.data) || {}
        this.errorMessage = body.message || TEXT.applyFailed
      } finally {
        this.busy = false
      }
    },

    // 결제는 받았는데 예약 인원이 안 늘었을 때: 서버가 주문 때 본 인원 그대로인지 보고 늘린다.
    async applyOnsite() {
      if (this.busy) return
      this.busy = true
      try {
        await this.$http.post(`/api/addon-pay/onsite/${this.order.orderId}/apply`, {}, { timeout: REQUEST_TIMEOUT_MS })
        this.applyError = ""
        this.finish({ applied: true })
      } catch (error) {
        const body = (error && error.response && error.response.data) || {}
        this.applyError = body.message || TEXT.applyFailed
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped src="./addonPayDialog.css"></style>
<style scoped>
.pick-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
}
.pick-row + .pick-row {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.pick-label {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.pick-name {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 700;
  word-break: keep-all;
}
.pick-hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  word-break: keep-all;
}
.pick-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
}
.pick-btn.v-btn {
  width: 44px;
  height: 44px;
  border-color: rgba(125, 24, 50, 0.4);
  color: #7d1832;
}
.pick-count {
  min-width: 40px;
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.quote-box {
  position: relative;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.quote-box.is-waiting {
  min-height: 48px;
}
/* 움직임 줄이기 설정이면 진행 줄은 움직이지 않는 막대로 둔다(묻는 중이라는 표시는 남긴다) */
@media (prefers-reduced-motion: reduce) {
  .quote-wait >>> .v-progress-linear__indeterminate {
    animation: none !important;
    left: 0 !important;
    right: 70% !important;
  }
}
/* 수를 바꿔 다시 묻는 동안 옛 금액은 흐리게(틀린 금액을 그대로 말하지 않게) */
.diff-table.is-stale {
  opacity: 0.45;
}
.quote-wait {
  position: absolute;
  top: -1px;
  left: 0;
  right: 0;
}
.diff-table dt {
  display: grid;
  gap: 2px;
  min-width: 0;
  word-break: keep-all;
}
.diff-sub {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 400;
}
</style>
